import * as React from 'react';
import { SelectOption } from 'formComponents/Select/types';
import { QueryResult } from 'react-query';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';

export interface BetreuerWorkTime {
    betrNr: number;
    weekday: number; // 0 = monday, ... 6 = sunday
    block: number;
    startTime: number | null; // minutes since midnight
    startTimeS: string | undefined; // temporary for editing
    stopTime: number | null;
    stopTimeS: string | undefined;
}

export const defaultBetreuerWorkTime: BetreuerWorkTime = {
    betrNr: 0,
    weekday: 0,
    block: 0,
    startTime: 0,
    startTimeS: '',
    stopTime: 0,
    stopTimeS: '',
};

export interface Betreuer {
    nr: number;
    kz: string;
    name: string;
    firma: string;
    strasse: string;
    plz: string;
    ort: string;
    land: string;
    telefon: string;
    mobil: string;
    mail: string;
    provision: number;
    aktiv: boolean;
    rolle: string;
    holidayHours: number;
    updDat?: string;
    updUhr?: number;
    updUser?: string;
    betreuerWorkTimeList: BetreuerWorkTime[];
}

export const defaultBetreuer: Betreuer = {
    nr: -1,
    kz: '',
    name: '',
    firma: '',
    strasse: '',
    plz: '',
    ort: '',
    land: '',
    telefon: '',
    mobil: '',
    mail: '',
    provision: 0,
    aktiv: true,
    rolle: 'ID',
    holidayHours: 0,
    betreuerWorkTimeList: [],
};

export interface BetreuerFilter {
    nr?: number;
    aktiv?: boolean;
    withBetreuerWorkTimeList?: boolean;
}

export const betreuerFilterItems: SelectOption[] = [
    { value: '0', label: 'Alle Kunden' },
    { value: '-1', label: 'Meine Kunden und weiße Kunden' },
    { value: '-2', label: 'Nur meine Kunden' },
    { value: '-3', label: 'Nur weiße Kunden' },
    { value: '-4', label: 'Archivierte Kunden' },
    { value: '-5', label: 'Alle Kunden (inkl. archivierte)' },
];

interface AuthenticateResult extends Betreuer {
    isAuthenticated: true;
}

export async function authenticate(mail: string, password: string): Promise<AuthenticateResult> {
    return callApi<AuthenticateResult>(Controller.authenticate, { mail, passwort: password });
}

export async function validate(): Promise<AuthenticateResult> {
    return callApi<AuthenticateResult>(Controller.authenticate + '/validate', {});
}

export function useBetreuer(filter?: BetreuerFilter): Betreuer[] {
    const query = useApi<Betreuer[]>(Controller.lookup + '/betreuer', { filter });

    return React.useMemo<Betreuer[]>(() => {
        if (query.isLoading || query.isError || !query.data) {
            return [];
        }
        return query.data;
    }, [query]);
}

export function useBetreuerAsOptions(): SelectOption[] {
    const betreuerList = useBetreuer();

    return React.useMemo<SelectOption[]>(() => {
        return betreuerList
            .filter((b) => b.aktiv)
            .map((betreuer) => ({ value: betreuer.nr.toString(), label: betreuer.name } as SelectOption));
    }, [betreuerList]);
}

export function useBetreuerByNr(): { [key: number]: Betreuer } {
    const betreuerList = useBetreuer();

    return React.useMemo<{ [key: number]: Betreuer }>(() => {
        return betreuerList.reduce<{ [key: number]: Betreuer }>(
            (acc, user) => ({
                ...acc,
                [user.nr]: user,
            }),
            {},
        );
    }, [betreuerList]);
}

export function useBetreuerByKz(): { [key: string]: Betreuer } {
    const betreuerList = useBetreuer();

    return React.useMemo<{ [key: string]: Betreuer }>(() => {
        return betreuerList.reduce<{ [key: string]: Betreuer }>(
            (acc, user) => ({
                ...acc,
                [user.kz]: user,
            }),
            {},
        );
    }, [betreuerList]);
}

export function useBetreuerList(
    filter?: BetreuerFilter,
    apiOptions?: UseApiOptions<Betreuer[]>,
): QueryResult<Betreuer[]> {
    return useApi<Betreuer[]>(Controller.betreuer + '/list', { filter }, apiOptions);
}

export function useBetreuerOne(filter?: BetreuerFilter, apiOptions?: UseApiOptions<Betreuer>): QueryResult<Betreuer> {
    return useApi<Betreuer>(Controller.betreuer + '/one', { filter }, apiOptions);
}

interface AddBetreuerResult {
    newNr: number;
}

export async function addBetreuer(betreuer: Betreuer): Promise<AddBetreuerResult> {
    return callApi<AddBetreuerResult>(Controller.betreuer + '/add', { betreuer });
}

export async function updateBetreuer(betreuer: Betreuer): Promise<void> {
    return callApi<void>(Controller.betreuer + '/update', { betreuer });
}
